import { type DragEvent, useCallback, useState } from 'react';
import { type Edge, type Node, type ReactFlowInstance } from '@xyflow/react';
import * as Api from '@ViewModels';
import { toast } from 'sonner';
import type { BaseNodeData, FlowContact } from '../types';
import { type BranchEdgeData, EDGE_TYPES, type PhantomAddPathEdgeData } from '../types/edges';
import type { SegmentNodeData } from '../types/stepNodes';
import { NODE_TYPES } from '../types';
import type { ChainTerminus } from './useChainTermini';
import type { HistoryActionType } from './useUndoRedo';
import { addNodeWithLayout, findClosestTerminus, insertNodeAfter } from '../utils/autoLayout';
import { applyLayout } from '../utils/layoutEngine';
import { reconcileOptimisticStepNode } from '../utils/reconcileOptimisticStepNode';
import { scheduleCenterOnNode } from '../utils/viewport';
import { wouldCreateCycle } from '../utils/layoutHelpers';
import {
	createSegmentNode,
	createSwitchStepNode,
	createTriggerNode,
	createStepNodeByType,
	generateNodeId,
} from '../utils/nodeFactory';
import { BRANCH_MARGIN, BRANCH_VERTICAL_GAP, NODE_WIDTH, SEGMENT_NODE } from '../utils/layoutConstants';

const DEFAULT_PARENT_HEIGHT = 120;

/**
 * Aligns switch segment nodes horizontally below their parent switch node.
 * Ensures consistent spacing and positioning of branch lanes.
 */
function alignSwitchSegments(
	nodes: Node<BaseNodeData>[],
	switchNodeId: string,
	visualParentId?: string
): Node<BaseNodeData>[] {
	const segments = nodes.filter(node => {
		if (node.type !== NODE_TYPES.SEGMENT) {
			return false;
		}
		const data = node.data as SegmentNodeData;
		return data.parentSwitchNodeId === switchNodeId;
	});

	if (segments.length <= 1) {
		return nodes;
	}

	const sortedSegments = [...segments].sort((a, b) => {
		const aIndex = (a.data as SegmentNodeData).segmentIndex;
		const bIndex = (b.data as SegmentNodeData).segmentIndex;
		return aIndex - bIndex;
	});

	const parentNode = nodes.find(node => node.id === visualParentId) ?? nodes.find(node => node.id === switchNodeId);
	if (!parentNode) {
		return nodes;
	}

	const parentWidth = parentNode.measured?.width ?? parentNode.width ?? NODE_WIDTH;
	const parentHeight = parentNode.measured?.height ?? parentNode.height ?? DEFAULT_PARENT_HEIGHT;
	const parentCenterX = parentNode.position.x + parentWidth / 2;

	const branchSpacing = SEGMENT_NODE.WIDTH + BRANCH_MARGIN;
	const startCenterX = parentCenterX - ((sortedSegments.length - 1) * branchSpacing) / 2;
	const targetY = parentNode.position.y + parentHeight + BRANCH_VERTICAL_GAP;

	const positionOverrides = new Map<string, { x: number; y: number }>();
	for (let index = 0; index < sortedSegments.length; index++) {
		const segment = sortedSegments[index];
		const laneCenterX = startCenterX + index * branchSpacing;
		positionOverrides.set(segment.id, {
			x: laneCenterX - SEGMENT_NODE.WIDTH / 2,
			y: targetY,
		});
	}

	const lastSegment = sortedSegments[sortedSegments.length - 1];
	const phantomTargetId = `phantom-target-${switchNodeId}`;
	const lastSegmentPosition = positionOverrides.get(lastSegment.id);
	if (lastSegmentPosition) {
		positionOverrides.set(phantomTargetId, {
			x: lastSegmentPosition.x + SEGMENT_NODE.WIDTH + 80,
			y: lastSegmentPosition.y,
		});
	}

	if (positionOverrides.size === 0) {
		return nodes;
	}

	return nodes.map(node => {
		const override = positionOverrides.get(node.id);
		if (!override) {
			return node;
		}
		return {
			...node,
			position: override,
		};
	});
}

interface SwitchStepInsertResult {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	focusNodeId: string;
	switchNodeId: string;
}

/**
 * Builds a hidden switch step with a blank case + default case rendered as segment nodes.
 */
export function addSwitchStepWithSegments(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	position: { x: number; y: number },
	parentNodeId?: string
): SwitchStepInsertResult {
	const blankCaseId = generateNodeId('case');
	const defaultCaseId = generateNodeId('case');

	const blankCase = {
		id: blankCaseId,
		isDefault: false,
	} as Api.IAutomationStepCaseStatement;

	const defaultCase = {
		id: defaultCaseId,
		isDefault: true,
		filter: null,
	} as Api.IAutomationStepCaseStatement;

	const switchStep = {
		_type: 'SwitchAutomationStep',
		cases: [blankCase, defaultCase],
	} as Api.ISwitchAutomationStep;

	const switchNode = createSwitchStepNode(position, switchStep);
	switchNode.hidden = true;
	switchNode.data.summary = '2 branches';
	switchNode.data.outputHandles = ['default'];

	const segmentNodes = [
		createSegmentNode(blankCase, 0, switchNode.id, position),
		createSegmentNode(defaultCase, 1, switchNode.id, position),
	];

	const visualSourceNodeId = parentNodeId ?? switchNode.id;
	const totalCases = segmentNodes.length;

	const branchEdges: Edge<BranchEdgeData>[] = segmentNodes.map((segment, index) => {
		const caseName = segment.data.isDefault ? 'Default' : `Case ${index + 1}`;
		return {
			id: `${visualSourceNodeId}-to-${segment.id}`,
			source: visualSourceNodeId,
			target: segment.id,
			type: EDGE_TYPES.BRANCH,
			data: {
				condition: segment.data.filter,
				caseId: segment.data.caseId,
				caseIndex: index,
				totalCases,
				isDefault: segment.data.isDefault,
				caseName,
				caseColor: segment.data.caseColor,
				switchNodeId: switchNode.id,
			},
		};
	});

	const phantomTargetId = `phantom-target-${switchNode.id}`;
	const phantomTargetNode: Node<BaseNodeData> = {
		id: phantomTargetId,
		type: 'phantomTarget',
		position,
		width: 1,
		height: 1,
		data: {
			label: '',
			category: 'action',
		},
	};

	const phantomEdge: Edge<PhantomAddPathEdgeData> = {
		id: `phantom-add-path-${switchNode.id}`,
		source: visualSourceNodeId,
		sourceHandle: 'default',
		target: phantomTargetId,
		type: EDGE_TYPES.PHANTOM_ADD_PATH,
		data: {
			switchNodeId: switchNode.id,
			segmentCount: totalCases,
		},
	};

	const updatedNodes = [...nodes, switchNode, ...segmentNodes, phantomTargetNode];
	const updatedEdges = [...edges, ...branchEdges, phantomEdge];
	const layoutResult = applyLayout(updatedNodes, updatedEdges);
	const alignedNodes = alignSwitchSegments(layoutResult.nodes, switchNode.id, parentNodeId ?? switchNode.id);

	return {
		nodes: alignedNodes,
		edges: layoutResult.edges,
		focusNodeId: segmentNodes[0].id,
		switchNodeId: switchNode.id,
	};
}

interface UseCanvasDragDropParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	chainTermini: ChainTerminus[];
	autoConnect: boolean;
	animateEdges: boolean;
	activeContacts: FlowContact[];
	screenToFlowPosition: ReactFlowInstance['screenToFlowPosition'];
	setCenter: ReactFlowInstance['setCenter'];
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	onAddStepToChain?: (
		terminus: ChainTerminus | null,
		stepType: string
	) => Promise<Api.IAutomationStep | null | undefined>;
	/** The automation template - needed for creating new step nodes with proper context */
	automationTemplate?: Api.IAutomationTemplate;
}

interface UseCanvasDragDropReturn {
	closestTerminusId: string | null;
	onDragOver: (event: DragEvent<HTMLDivElement>) => void;
	onDragLeave: () => void;
	onDrop: (event: DragEvent<HTMLDivElement>) => Promise<void>;
}

/**
 * Hook that handles drag-and-drop functionality for adding nodes to the canvas.
 * Extracted from AutomationFlowCanvas to improve maintainability.
 */
export function useCanvasDragDrop({
	nodes,
	edges,
	setNodes,
	setEdges,
	chainTermini,
	autoConnect,
	animateEdges,
	activeContacts,
	screenToFlowPosition,
	setCenter,
	takeSnapshot,
	onAddStepToChain,
	automationTemplate,
}: UseCanvasDragDropParams): UseCanvasDragDropReturn {
	const [closestTerminusId, setClosestTerminusId] = useState<string | null>(null);

	const onDragOver = useCallback(
		(event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			event.dataTransfer.dropEffect = 'move';

			const position = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});
			const closest = findClosestTerminus(position, chainTermini);
			setClosestTerminusId(closest?.nodeId ?? null);
		},
		[screenToFlowPosition, chainTermini]
	);

	const onDragLeave = useCallback(() => {
		setClosestTerminusId(null);
	}, []);

	const onDrop = useCallback(
		async (event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setClosestTerminusId(null);

			const nodeType = event.dataTransfer.getData('application/reactflow');
			if (!nodeType) {
				return;
			}

			const position = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});

			const closestTerminus = findClosestTerminus(position, chainTermini);

			// Handle switch step specially - creates segments
			if (nodeType === NODE_TYPES.SWITCH_STEP) {
				// Only one conditional/switch node allowed per automation flow
				if (nodes.some(node => node.type === NODE_TYPES.SWITCH_STEP)) {
					return;
				}

				takeSnapshot('node_add', `Add ${nodeType} step`);

				const parentNodeId =
					closestTerminus?.nodeId ?? (autoConnect && nodes.length > 0 ? nodes[nodes.length - 1].id : undefined);
				const parentNode = parentNodeId ? nodes.find(node => node.id === parentNodeId) : null;
				const basePosition = parentNode?.position ?? position;

				const switchResult = addSwitchStepWithSegments(nodes, edges, basePosition, parentNodeId);

				const switchEdges = switchResult.edges.map(edge => ({
					...edge,
					animated: animateEdges,
					data: { ...edge.data, activeContacts },
				}));

				setNodes(switchResult.nodes);
				setEdges(switchEdges);

				const focusNode = switchResult.nodes.find(node => node.id === switchResult.focusNodeId);
				if (focusNode) {
					scheduleCenterOnNode(setCenter, focusNode, { duration: 300, zoom: 1 });
				}

				if (onAddStepToChain) {
					const createdStep = await onAddStepToChain(closestTerminus ?? null, nodeType);
					if (createdStep?.id) {
						setNodes(currentNodes =>
							currentNodes.map(node => {
								if (node.id !== switchResult.switchNodeId) {
									return node;
								}
								return {
									...node,
									data: {
										...node.data,
										stepId: createdStep.id,
										automationStep: createdStep,
										automationTemplate,
										stepData: {
											...(node.data as { stepData?: Record<string, unknown> }).stepData,
											name: createdStep.name,
										},
									},
								};
							})
						);

						const realSwitchNodeId = `step-${createdStep.id}`;
						setEdges(currentEdges =>
							currentEdges.map(edge => {
								if (
									edge.type !== EDGE_TYPES.PHANTOM_ADD_PATH ||
									(edge.data as { switchNodeId?: string })?.switchNodeId !== switchResult.switchNodeId
								) {
									return edge;
								}
								return {
									...edge,
									data: {
										...edge.data,
										switchNodeId: realSwitchNodeId,
									},
								};
							})
						);
					}
				}
				return;
			}

			// Resolve child template for branch steps.
			// Steps in a branch belong to the case's child template, not the root.
			let nodeTemplate = automationTemplate;
			if (closestTerminus?.branchInfo && automationTemplate) {
				const switchNode = nodes.find(n => n.id === closestTerminus.branchInfo!.parentSwitchId);
				const switchStep = switchNode?.data?.automationStep as Api.ISwitchAutomationStep | undefined;
				if (switchStep?.cases) {
					const matchedCase = closestTerminus.branchInfo.caseId
						? switchStep.cases.find(c => c.id === closestTerminus.branchInfo!.caseId)
						: switchStep.cases[closestTerminus.branchInfo.caseIndex];
					if (matchedCase?.automationTemplateId) {
						nodeTemplate = { ...automationTemplate, id: matchedCase.automationTemplateId };
					}
				}
			}

			// Create node based on type
			const newNode =
				nodeType === NODE_TYPES.TRIGGER
					? createTriggerNode(position)
					: createStepNodeByType(nodeType, position, undefined, nodeTemplate);

			if (!newNode) {
				toast.error(`Unknown node type: ${nodeType}`);
				return;
			}

			takeSnapshot('node_add', `Add ${nodeType} step`);

			let result: { nodes: Node<BaseNodeData>[]; edges: Edge[] };

			if (closestTerminus) {
				// All terminus nodes (segments, step nodes) use a source handle with
				// id='default'. The 'case-X' handles only exist on switch step nodes,
				// which are never termini. Pass undefined to use the default handle.
				result = insertNodeAfter(nodes, edges, closestTerminus.nodeId, newNode, undefined);
			} else if (autoConnect && nodes.length > 0) {
				const lastNode = nodes[nodes.length - 1];
				const connectToId = wouldCreateCycle(lastNode.id, newNode.id, edges) ? undefined : lastNode.id;
				result = addNodeWithLayout(nodes, edges, newNode, connectToId);
			} else {
				result = addNodeWithLayout(nodes, edges, newNode);
			}

			const updatedEdges = result.edges.map(edge => ({
				...edge,
				animated: animateEdges,
				data: { ...edge.data, activeContacts },
			}));

			setNodes(result.nodes);
			setEdges(updatedEdges);

			const addedNode = result.nodes.find(n => n.id === newNode.id);
			if (addedNode) {
				scheduleCenterOnNode(setCenter, addedNode, { duration: 300, zoom: 1 });
			}

			// Persist to backend if handler provided
			if (onAddStepToChain) {
				const createdStep = await onAddStepToChain(closestTerminus ?? null, nodeType);
				if (createdStep?.id) {
					reconcileOptimisticStepNode({
						optimisticNodeId: newNode.id,
						createdStep,
						automationTemplate: nodeTemplate,
						setNodes,
						setEdges,
					});
				}
			}
		},
		[
			screenToFlowPosition,
			setNodes,
			setEdges,
			nodes,
			edges,
			chainTermini,
			autoConnect,
			animateEdges,
			activeContacts,
			setCenter,
			takeSnapshot,
			onAddStepToChain,
			automationTemplate,
		]
	);

	return {
		closestTerminusId,
		onDragOver,
		onDragLeave,
		onDrop,
	};
}
